import React, { useEffect, useState } from 'react';

/** layouts */
import {
    FullLayout,
    FullLayoutProps,
    SnackbarCloseButton,
    useAvatarUrlDispatch,
} from '@europrocurement/flexy-components';

/** end layouts */

/** pages */
import { Box } from '@mui/material';
import { SnackbarAction, SnackbarKey, SnackbarProvider } from 'notistack';
import {
    B2DSymboleIcon,
    companyIcon,
    L2DFLogoIcon,
    L2DFLogoSmallIcon,
    plusIcon,
} from '@europrocurement/l2d-icons';
import { useLocation, useNavigate } from 'react-router';
import {
    ManageAvatar,
    parcoursFormalitePathResolver,
    useDefaultContextActions,
    useMessagerieNotification,
    OfferProvider,
} from '@europrocurement/l2d-modules';
import {
    setPrescripteurs,
    usePrescripteursCustomizer,
    useSociete,
    useTiersService,
    useUtilisateurService,
} from '@europrocurement/l2d-domain';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { FullLayoutProvider } from '@europrocurement/flexy-components/components/templates/FullLayout/FullLayoutContext';
import { grilleTarifaireBasePath } from '@europrocurement/l2d-modules/modules/GrilleTarifaire/constants';

import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { sha256 } from '@europrocurement/l2d-utils';
import { IconBadgeListItem } from '@europrocurement/flexy-components/components/molecules/IconBadgeList';
import { GlobalDataProvider } from '@europrocurement/l2d-modules/providers/GlobalDataProvider';
import L2DFCustomHeader from './L2DFCustomHeader';
import { useUserPreferences } from '../hook/useUserPreferences';
import usePrescriber from '../hook/usePrescriber';

/** end pages */

/** menu */

/** end menu */

const L2DFlayout: React.FunctionComponent<Omit<FullLayoutProps, 'logo' | 'logoFolded'>> = function (
    props,
) {
    const ToCaptureRef = React.useRef<HTMLDivElement>(null); // ref

    const closeBtn: SnackbarAction = (key: SnackbarKey) => (
        <SnackbarCloseButton snackbarKey={key} />
    );

    const navigate = useNavigate();
    const location = useLocation();

    const kc = UseKeycloakService();

    const { prescriberId, prescriberIds } = usePrescriber();
    const { prescribersApi } = useTiersService();
    const xIdSociete = useSociete();
    const dispatch = useDispatch();
    const prescribersCustomizer = usePrescripteursCustomizer();
    const { customMenuItems, renderCurrentSelection, activeItem } = useUserPreferences();

    const { avatarBaseUrl } = useUtilisateurService();

    const avatarUrlDispatch = useAvatarUrlDispatch();

    const { frontContextActions } = useDefaultContextActions();

    const [userIdentifier, setUserIdentifier] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        const token = kc.getTokenParsed();
        if (token && token.preferred_username && token.preferred_username !== userIdentifier) {
            setUserIdentifier(token.preferred_username);
        }
    }, [kc, avatarBaseUrl, userIdentifier]);

    React.useEffect(() => {
        const updateAvatar = async (newIdentifier: string) => {
            const keycloakIdHash = await sha256(newIdentifier);

            avatarUrlDispatch({
                type: 'update',
                payload: `${avatarBaseUrl + keycloakIdHash}?time=${new Date().getTime()}`,
            });
        };
        if (userIdentifier) {
            updateAvatar(userIdentifier);
        }
    }, [avatarBaseUrl, avatarUrlDispatch, userIdentifier]);

    useEffect(() => {
        if (location.pathname !== '/errors/noPrescriber' && prescriberId === undefined) {
            navigate('/errors/noPrescriber');
        }
    }, [location.pathname, navigate, prescriberId]);

    /**
     * Use effect qui va chercher tous les prescripteurs liés a l'utilisateur
     */
    useEffect(() => {
        /**
         * on compare les ids prescripteur du token et ceux du customize
         */
        const numberTokenIds = prescriberIds?.map((item) => Number(item)).sort();
        const numberCustoIds = prescribersCustomizer?.map((item) => Number(item.id)).sort();
        if (!_.isEqual(numberTokenIds, numberCustoIds)) {
            /**
             * si les tableaux sont diférents, on va chercher les prescripteurs
             */
            const proms = numberTokenIds?.map((id) =>
                prescribersApi.getTiersTiersItem({
                    id: `${id}`,
                    xIdSociete,
                }),
            );
            if (proms) {
                Promise.all(proms).then((res) => {
                    dispatch(
                        setPrescripteurs(
                            res
                                .filter((item) => item.status === 200)
                                .map((prescripteurRes) => ({
                                    id: prescripteurRes.data.id,
                                    raisonSociale: prescripteurRes.data.raisonSociale,
                                    siren: prescripteurRes.data.siren,
                                    siret: prescripteurRes.data.siret,
                                })),
                        ),
                    );
                });
            }
        }
    }, [dispatch, prescriberId, prescriberIds, prescribersApi, prescribersCustomizer, xIdSociete]);

    const [messageItemIconBadge, setMessageItemIconBadge] = useState<IconBadgeListItem[]>([]);
    const [notificationItemIconBadge, setNotificationItemIconBadge] = useState<IconBadgeListItem[]>(
        [],
    );

    const { messageItems, notificationItems } = useMessagerieNotification();

    useEffect(() => {
        if (messageItems && messageItems.length > 0) {
            setMessageItemIconBadge(messageItems);
        }
    }, [messageItems]);

    useEffect(() => {
        if (notificationItems && notificationItems.length > 0) {
            setNotificationItemIconBadge(notificationItems);
        }
    }, [notificationItems]);

    const layoutProps: FullLayoutProps = {
        ...props,
        defaultContextActions: frontContextActions,
        contextActionMainIcon: (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
            >
                <B2DSymboleIcon style={{ maxHeight: '24px' }} />
            </Box>
        ),
        logo: <L2DFLogoIcon style={{ width: '100%', height: '100%' }} />,
        logoFolded: <L2DFLogoSmallIcon style={{ width: '100%', height: '100%' }} />,
        sidebarActions: [
            {
                icon: { ...plusIcon.props },
                label: 'Nouveau Dossier',
                onClick: () => {
                    navigate(parcoursFormalitePathResolver('stepFormalite'));
                },
            },
        ],
        pageTitle: 'Legal2digital Formalités',
    };
    const navigateToGrilleTarifaire = () => {
        navigate(grilleTarifaireBasePath);
    };

    return (
        <SnackbarProvider
            maxSnack={3}
            autoHideDuration={3000}
            anchorOrigin={{
                horizontal: 'center',
                vertical: 'bottom',
            }}
            action={closeBtn}
            variant="success"
        >
            <GlobalDataProvider
                validators={false}
                users={false}
            >
                <div ref={ToCaptureRef}>
                    <OfferProvider>
                        <FullLayoutProvider>
                            <FullLayout
                                {...layoutProps}
                                headerProps={{
                                    showPrescripteur: true,
                                    showSociete: false,
                                    customHeader: <L2DFCustomHeader />,
                                    otherEntries: [
                                        {
                                            label: 'Ma grille Tarifaire',
                                            icon: companyIcon,
                                            actionButton: navigateToGrilleTarifaire,
                                        },
                                    ],
                                    manageAvatar: <ManageAvatar />,
                                    itemsMessage: messageItemIconBadge,
                                    itemsNotification: notificationItemIconBadge,
                                }}
                                userPreferencesProps={{
                                    customMenuItems,
                                    renderCurrentSelection,
                                    activeItem,
                                }}
                            />
                        </FullLayoutProvider>
                    </OfferProvider>
                </div>
            </GlobalDataProvider>
        </SnackbarProvider>
    );
};

export default L2DFlayout;
